"use client";
import { useAppDispatch, useAppSelector } from "../../lib/redux/hooks";
import { 
  changeEducations, 
  addSectionInForm, 
  deleteSectionInFormByIdx,
  moveSectionInForm,
  selectEducations 
} from "../../lib/redux/resumeSlice";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Plus, Trash2, ChevronUp, ChevronDown } from "lucide-react";

export const EducationsForm = () => {
  const dispatch = useAppDispatch();
  const educations = useAppSelector(selectEducations);

  const handleEducationChange = (
    idx: number,
    field: keyof typeof educations[0],
    value: string | string[]
  ) => {
    dispatch(changeEducations({ idx, field, value }));
  };

  const handleAddEducation = () => {
    dispatch(addSectionInForm({ form: "educations" }));
  };

  const handleDeleteEducation = (idx: number) => {
    dispatch(deleteSectionInFormByIdx({ form: "educations", idx }));
  };

  const handleMoveEducation = (idx: number, direction: "up" | "down") => {
    dispatch(moveSectionInForm({ form: "educations", idx, direction }));
  };

  return (
    <Card className="bg-white border-2 border-gray-800 shadow-lg">
      <CardHeader className="flex flex-row items-center justify-between bg-gray-900 text-white">
        <CardTitle className="text-lg font-semibold">Education</CardTitle>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleAddEducation}
          className="border-white text-white hover:bg-white hover:text-black transition-colors"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Education
        </Button>
      </CardHeader>
      <CardContent className="space-y-6">
        {educations.map((education, idx) => (
          <div key={idx} className="space-y-4 p-4 border-2 border-gray-300 rounded-lg bg-gray-50">
            <div className="flex items-center justify-between">
              <h4 className="font-semibold text-black">Education {idx + 1}</h4>
              <div className="flex items-center space-x-2">
                {educations.length > 1 && (
                  <>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => handleMoveEducation(idx, "up")}
                      disabled={idx === 0}
                      className="border-gray-400 text-gray-600 hover:border-black hover:text-black transition-colors"
                    >
                      <ChevronUp className="h-4 w-4" />
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => handleMoveEducation(idx, "down")}
                      disabled={idx === educations.length - 1}
                      className="border-gray-400 text-gray-600 hover:border-black hover:text-black transition-colors"
                    >
                      <ChevronDown className="h-4 w-4" />
                    </Button>
                  </>
                )}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => handleDeleteEducation(idx)}
                  disabled={educations.length === 1}
                  className="border-red-300 text-red-600 hover:border-red-600 hover:bg-red-50 transition-colors"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor={`school-${idx}`} className="text-black font-semibold">School/University</Label>
                <Input
                  id={`school-${idx}`}
                  value={education.school}
                  onChange={(e) => handleEducationChange(idx, "school", e.target.value)}
                  placeholder="University Name"
                  className="border-2 border-gray-300 bg-white text-black placeholder-gray-500 hover:border-black focus:border-black transition-colors"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor={`degree-${idx}`} className="text-black font-semibold">Degree</Label>
                <Input
                  id={`degree-${idx}`}
                  value={education.degree}
                  onChange={(e) => handleEducationChange(idx, "degree", e.target.value)}
                  placeholder="Bachelor of Science in Computer Science"
                  className="border-2 border-gray-300 bg-white text-black placeholder-gray-500 hover:border-black focus:border-black transition-colors"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor={`date-${idx}`} className="text-black font-semibold">Graduation Date</Label>
                <Input
                  id={`date-${idx}`}
                  value={education.date}
                  onChange={(e) => handleEducationChange(idx, "date", e.target.value)}
                  placeholder="May 2020"
                  className="border-2 border-gray-300 bg-white text-black placeholder-gray-500 hover:border-black focus:border-black transition-colors"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor={`gpa-${idx}`} className="text-black font-semibold">GPA (Optional)</Label>
                <Input
                  id={`gpa-${idx}`}
                  value={education.gpa}
                  onChange={(e) => handleEducationChange(idx, "gpa", e.target.value)}
                  placeholder="3.8/4.0"
                  className="border-2 border-gray-300 bg-white text-black placeholder-gray-500 hover:border-black focus:border-black transition-colors"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor={`descriptions-${idx}`} className="text-black font-semibold">Relevant Coursework & Achievements</Label>
              <Textarea
                id={`descriptions-${idx}`}
                value={education.descriptions.join('\n')}
                onChange={(e) => {
                  const descriptions = e.target.value.split('\n').filter(line => line.trim());
                  handleEducationChange(idx, "descriptions", descriptions);
                }}
                placeholder="• Data Structures and Algorithms
• Software Engineering Principles
• Dean's List (3.8+ GPA)"
                rows={3}
                className="border-2 border-gray-300 bg-white text-black placeholder-gray-500 hover:border-black focus:border-black transition-colors resize-none"
              />
              <p className="text-sm text-gray-600">
                Enter each item on a new line starting with a bullet point (•)
              </p>
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};
